import store from "../../store"
import _ from "lodash"
import { useSelector } from "react-redux"
import { userSelector } from "@redux/user"
import { useMemo } from "react"
import { EVENT_TYPE_COLORS } from "@/const"
import { useGetRootGroupQueryL } from "@api/rootGroup-api"
import { useOperationalClass, useRGCDic } from "@utils/hooks"
import { useGetTemplateRolesQueryL } from "@api/templaterole-api"
import { useGetEventTypesQueryL } from "@api/eventType-api"
import { loc } from "@utils/localization"
import type { PageCustomization } from "@myeway/types"
export const SPORT_PLAYER_ROLE_NAME = "player"
export const WELLBEING_OPERATIONAL_NAME = "hyvinvointi"
export const WELLBEING_PROCESS_NAME = "k-hyvinvointi-pilotti"
export const WELLBEING_COACH_ROLE_NAME = "hyvinvointi_valmentaja"
export const WELLBEING_PARTICIPANT_ROLE_NAME = "hyvinvointi_osallistuja"

export const EVENT_PATHS = {
	[WELLBEING_OPERATIONAL_NAME]: "/wb/events/",
	football: "/events/",
	floorball: "/events/",
	futsal: "/events/",
}

/**
 * Returns the name of the operational class.
 *
 * NOTE: Due to not being a hook with API query, will not function for
 * admin users with that have selected a root group context in the role selector.
 *
 * @returns {String} The name of the operational class.
 */
export const getOperationalClassName = () => {
	const state = store.getState()
	const profileDataForCtx = _.get(state, "user.profileDataForCtx")
	if (!profileDataForCtx) {
		return null
	}
	const opClass = profileDataForCtx.operationalClass
	const { name } = opClass || {}
	return name
}

export const useOperationalClassName = () => {
	const { profileDataForCtx } = useSelector(userSelector)
	if (!profileDataForCtx) {
		return null
	}
	const opClass = profileDataForCtx.operationalClass
	const { name } = opClass || {}
	return name
}
/**
 * Variant of `getFFAClubSupportBucketLevel` that utilizes a query. This fixes issues with synchronization for context switching.
 *
 * Important to note; because this is an asynchronous hook, "undefined" is returned when data is not yet received. "null" means data was received but bucket level not found.
 * */
export const useGetFFAClubSupportBucketLevelByRootGroup = (rootGroupId: string) => {
	const BUCKET1 = "kori1"
	const BUCKET2 = "kori2"
	const BUCKET3 = "kori3"

	/** @type {1 | 2 | 3 | null} */
	let level = null

	const { data: rootGroup } = useGetRootGroupQueryL({ id: rootGroupId })
	if (!rootGroup) {
		return undefined
	}

	if (rootGroup?.classes?.find((level: { name: string }) => level.name === BUCKET1)) level = 1
	if (rootGroup?.classes?.find((level: { name: string }) => level.name === BUCKET2)) level = 2
	if (rootGroup?.classes?.find((level: { name: string }) => level.name === BUCKET3)) level = 3

	return level
}

/**
 * Returns whether user has access to Finnish Football Association's club support.
 *
 * This hook is a "broad" check, don't use this for checking if user can assign players, which is only available for bucket levels 1 and 2.
 *
 * Conditions are as follows:
 * 	1. Is a head coach (admins with head coach context are also allowed)
 *  2. Has a non-null bucket level (1, 2, 3)
 */
export const useHasAccessToFFAClubSupport = () => {
	const { contextData, isPlainAdmin, isAdminWithCtx, hasClubSupportAdmin } = useSelector(userSelector)
	const isHeadCoach = ["headcoach", "headcoach_rg"].includes(contextData?.groupRole)
	const bucketLevel = useGetFFAClubSupportBucketLevelByRootGroup(contextData?.rootGroup)

	if (isPlainAdmin || isAdminWithCtx || hasClubSupportAdmin) {
		return true
	}

	return !!bucketLevel && isHeadCoach
}
/**
 * Returns whether or not user is admin with a rootgroup context enabled
 */
export const useIsAdminWithContext = () => {
	const { selectedRole, contextData } = useSelector(userSelector)
	const isAdmin = isSuperUser(selectedRole)
	const hasRootGroupInContext = contextData.rootGroup
	return isAdmin && hasRootGroupInContext
}

/**
 * Returns whether or not user is admin (can be either contextless or contextful)
 */
export const useIsAdmin = () => {
	const { selectedRole } = useSelector(userSelector)
	const isAdmin = isSuperUser(selectedRole)
	return isAdmin
}
/**
 * Returns whether user is a coach or headcoach.
 */
export const getIsCoach = () => {
	const state = store.getState()
	const contextRole = _.get(state, "user.contextData.groupRole")
	return ["coach", "headcoach", "headcoach_rg"].includes(contextRole)
}
/** Returns whether user can be headcoach in their current rootgroup. Does not check whether they currently are set to that role in their context selection. */
export const useCanBeRootGroupHeadCoach = () => {
	const { profileDataForCtx, contextData } = useSelector(userSelector)
	return !!_.get(profileDataForCtx, ["groupRoleOverview", contextData?.rootGroup, "memberRoles"])?.some(
		(mr: { name: string }) => mr.name === "headcoach" || mr.name === "headcoach_rg"
	)
}

/** Returns whether user is currently in context a headcoach. */
export const useIsHeadCoach = () => {
	const { contextData } = useSelector(userSelector)
	return ["headcoach", "headcoach_rg"].includes(contextData?.groupRole)
}

/**
 * Returns to process class where parent is operational class.
 * Process class parents must include operational class _id.
 * @returns {Object} The process class.
 */
const useGetProcessClass = () => {
	const op = useOperationalClass()
	if (!op) {
		return null
	}
	const { profileDataForCtx } = useSelector(userSelector)
	return profileDataForCtx?.processClasses?.find((p: { parents: string[] }) => p.parents.includes(op._id))
}
/**
 * Returns all page customizations linked to current operational class.
 * @returns {object[]}
 */
export const useGetPageCustomizations = () => {
	const opClass = useOperationalClass()
	const pageCustomizations = _.get(opClass, "pageCustomizations")
	return pageCustomizations
}

/**
 * Returns one page customization by section name. If cannot be found, returns undefined.
 * @param {string} section - Section name (e.g. *physicalDevelopmentGraphs*)
 * @returns {import("@myeway/types").PageCustomization}
 */
export const useGetPageCustomizationBySection = (section: string) => {
	const pageCustomizations = useGetPageCustomizations()
	return pageCustomizations.find((o: PageCustomization) => o.section === section)
}

/**
 * Returns the names of the process classes.
 * @returns {String} The names of the process classes.
 */
export const getProcessNames = () => {
	const state = store.getState()
	const profileDataForCtx = _.get(state, "user.profileDataForCtx")
	if (!profileDataForCtx) {
		return []
	}
	return profileDataForCtx.processClasses.map((p: { name: string }) => p.name)
}

/**
 * Returns the features.
 * @returns {Array[String]} The features.
 */
export const getFeatures = () => {
	const state = store.getState()
	const profileDataForCtx = _.get(state, "user.profileDataForCtx")
	if (!profileDataForCtx) {
		return []
	}
	return profileDataForCtx?.features ?? []
}

export const useFeatures = () => {
	const { profileDataForCtx } = useSelector(userSelector)
	if (!profileDataForCtx) {
		return []
	}
	return profileDataForCtx?.features ?? []
}

/**
 * Returns the product levels.
 * @returns {Array[String]} The product levels.
 */
export const getProductLevels = () => {
	const state = store.getState()
	const profileDataForCtx = _.get(state, "user.profileDataForCtx")
	if (!profileDataForCtx) {
		return []
	}
	return profileDataForCtx.levelClasses
}

export const useProductLevels = () => {
	const { profileDataForCtx } = useSelector(userSelector)
	if (!profileDataForCtx) {
		return []
	}
	return profileDataForCtx.levelClasses
}

/**
 * Get group role in context.
 * @returns {String}
 */
export const getGroupRole = () => {
	const state = store.getState()
	const contextData = _.get(state, "user.contextData")
	if (!contextData) {
		return null
	}
	return contextData.groupRole
}

/**
 * Returns combined personal and group event types
 * from operational class and process class.
 *
 * The return value is dependent on the user's current context.
 *
 * @returns {Object} The event types.
 */
export const useGetCombinedEventTypes = () => {
	let op = useOperationalClass()
	let process = useGetProcessClass()

	if (!op) {
		op = { personalEventTypes: [], eventTypes: [] }
	}
	const opPersonalEventTypes = op.personalEventTypes
	const opGroupEventTypes = op.eventTypes

	if (!process) {
		process = { personalEventTypes: [], eventTypes: [] }
	}
	const processPersonalEventTypes = process.personalEventTypes
	const processGroupEventTypes = process.eventTypes
	// Combine op and process event types also mark if event type is from process or op
	const personalEventTypes = opPersonalEventTypes
		.map((et: any) => ({ ...et, source: "operational" }))
		.concat(processPersonalEventTypes.map((et: any) => ({ ...et, source: "process" })))
		.map((o: any) => loc(o))
	const groupEventTypes = opGroupEventTypes
		.map((et: any) => ({ ...et, source: "operational" }))
		.concat(processGroupEventTypes.map((et: any) => ({ ...et, source: "process" })))
		.map((o: any) => loc(o))

	return { personalEventTypes, groupEventTypes }
}

/**
 * Returns combined personal and group event types
 * from operational class and process class.
 *
 * The return value is dependent on the input root group id.
 *
 * @returns {any} The event types.
 */
export const useGetCombinedEventTypesInRoot = (rootGroupId?: string | null) => {
	const RGCDic = useRGCDic(rootGroupId)
	let op = RGCDic["operational"]
	const processes = RGCDic["process"]
	const productLvls = RGCDic["productLvl"]

	if (!op) {
		op = { personalEventTypes: [], eventTypes: [] }
	}
	const opPersonalEventTypes = op.personalEventTypes
	const opGroupEventTypes = op.eventTypes

	const processPersonalEventTypes = processes.reduce((a: any, b: any) => a.concat(b.personalEventTypes), [])
	const processGroupEventTypes = processes.reduce((a: any, b: any) => a.concat(b.eventTypes), [])

	const prodLvlPersonalEventTypes = productLvls.reduce((a: any, b: any) => a.concat(b.personalEventTypes), [])
	const prodLvlGroupEventTypes = productLvls.reduce((a: any, b: any) => a.concat(b.eventTypes), [])

	// Combine op and process event types also mark if event type is from process or op
	const personalEventTypes = opPersonalEventTypes
		.map((et: any) => ({ ...et, source: "operational", origin: "personal" }))
		.concat(processPersonalEventTypes.map((et: any) => ({ ...et, source: "process", origin: "personal" })))
		.concat(prodLvlPersonalEventTypes.map((et: any) => ({ ...et, source: "productLvl", origin: "personal" })))
		.map((o: any) => loc(o))
	const groupEventTypes = opGroupEventTypes
		.map((et: any) => ({ ...et, source: "operational", origin: "group" }))
		.concat(processGroupEventTypes.map((et: any) => ({ ...et, source: "process", origin: "group" })))
		.concat(prodLvlGroupEventTypes.map((et: any) => ({ ...et, source: "productLvl", origin: "group" })))
		.map((o: any) => loc(o))
	return { personalEventTypes, groupEventTypes }
}

/**
 * Returns all personal and group event types
 */
export const useGetCombinedEventTypesAll = () => {
	const { data } = useGetEventTypesQueryL()
	if (!data) {
		return { personalEventTypes: [], groupEventTypes: [] }
	}
	const personalEventTypes = data.reduce((a: any, b: any) => {
		const et = b
		const personalMatch = et.classes?.find((c: any) => c.name === "personal")
		if (personalMatch) {
			a.push({ ...et, origin: "personal" })
		}
		return a
	}, [])
	const groupEventTypes = data.reduce((a: any, b: any) => {
		const et = b
		const groupMatch = et.classes?.find((c: any) => c.name === "group")
		if (groupMatch) {
			a.push({ ...et, origin: "group" })
		}
		return a
	}, [])

	return { personalEventTypes, groupEventTypes }
}

export const useEventColors = () => {
	const { personalEventTypes, groupEventTypes } = useGetCombinedEventTypesAll() ?? {}
	const colorDic = (groupEventTypes ?? []).concat(personalEventTypes ?? []).reduce((a: any, b: any) => {
		const eventType = b
		const name = eventType.name
		const color = eventType.color
		return { ...a, [name]: color }
	}, {})

	const fallbackDic = EVENT_TYPE_COLORS
	const dic = { ...fallbackDic, ...colorDic }
	return { dic, getColor: (type?: string) => dic[type ?? ""] ?? "#000000" }
}

/**
 * Has the user the given group role?
 * @param groupRole String
 * @returns {boolean}
 */
export const hasGroupRole = (groupRole: string | null) => {
	return getGroupRole() === groupRole
}

/**
 * Does the user have the product level?
 * @param productLevel
 * @returns {boolean}
 */
export const hasProductLevel = (productLevel: string) => {
	return getProductLevels().includes(productLevel)
}

// Wellbeing specific functions
/**
 * Is the operational class wellbeing?
 * @returns {boolean}
 */
export const isWellbeingOperational = () => {
	if (getOperationalClassName() === WELLBEING_OPERATIONAL_NAME) {
		return true
	}
	return false
}

export const useIsWellbeingOperational = () => {
	const opClassName = useOperationalClassName()
	if (opClassName === WELLBEING_OPERATIONAL_NAME) {
		return true
	}
	return false
}

/**
 * Does the user have the wellbeing process?
 * @returns {boolean}
 */
export const hasWellbeingProcess = () => {
	if (getProcessNames().includes(WELLBEING_PROCESS_NAME)) {
		return true
	}
	return false
}

/**
 * Has the user a wellbeing related group role?
 *
 * Use `hasWellbeingCoachRole` or `hasWellbeingParticipantRole` for more fine-grained control.
 * @returns {boolean}
 */
export const hasWellbeingRole = () => {
	return getGroupRole() === WELLBEING_PARTICIPANT_ROLE_NAME || getGroupRole() === WELLBEING_COACH_ROLE_NAME
}

export const useHasWellbeingRole = () => {
	const { contextData } = useSelector(userSelector)
	return contextData.groupRole === WELLBEING_PARTICIPANT_ROLE_NAME || contextData.groupRole === WELLBEING_COACH_ROLE_NAME
}
const useActiveRole = () => {
	const { profileDataForCtx } = useSelector(userSelector)
	if (!profileDataForCtx) {
		return null
	}
	return profileDataForCtx.activeMemberRole
}
/** Returns whether or not is a player. Uses `useSelector` */
export const useIsPlayer = () => {
	const curRole = useActiveRole()
	return curRole?.name === SPORT_PLAYER_ROLE_NAME
}

export const usePlayerLike = () => {
	const curRole = useActiveRole()
	return !!curRole?.generalPerms?.includes("playerLike")
}
export const useCoachLike = () => {
	const curRole = useActiveRole()
	return !!curRole?.generalPerms?.includes("coachLike")
}

export const usePlayerLikeRoleNames = () => {
	const { data } = useGetTemplateRolesQueryL()
	return useMemo(() => {
		if (!data) {
			return []
		}
		return data.filter((o) => o.generalPerms?.includes("playerLike")).map((o) => o.name)
	}, [data])
}
/**
 * Whether user has elevated access. This currently means that
 * the user has either 'expert' or 'admin' role.
 */
export const isSuperUser = (selectedRole?: string) => {
	if (!selectedRole) {
		selectedRole = store.getState().user.selectedRole
	}
	return ["admin", "expert"].includes(selectedRole ?? "")
}
